import { Ability, AbilityBuilder, AbilityClass } from '@casl/ability'
import { IUser } from 'redux/store/user/types'
import { ROLES } from 'features/Permission'
import { ACTIONS, SUBJECTS } from './constants'
import {
  BankAccountAbilityBuilder,
  BankAccountAbility,
  PossibleAbilities,
  Conditions
} from './types'
import {
  isInPurposes,
  isInReasons,
  isInStatuses,
  isNotDefault,
  isNotInReasons
} from './utils'
import { BANK_ACCOUNT_STATUS } from '../BankAccountsTable/constants'
import {
  ACCOUNT_PURPOSE_OPTIONS,
  BANK_ACCOUNT_DETAILS_STATUS_REASONS
} from '../BankAccountDetails/constants'

const BankAccountAbilityClass = Ability as AbilityClass<BankAccountAbility>

export const ability = new Ability<PossibleAbilities, Conditions>()

export default (user: IUser) => {
  const builder = new AbilityBuilder(BankAccountAbilityClass)

  switch (user.role) {
    case ROLES.VENDOR_CONTRACT_ADMIN:
    case ROLES.VENDOR_FINANCE_OFFICER:
    case ROLES.VENDOR_ANALYST:
    case ROLES.VENDOR_CONTRACT_STEWARD:
      defineVendorsAbilities(builder, user)
      break
    case ROLES.HOSPITAL_ADMIN:
    case ROLES.VICE_PRESIDENT:
    case ROLES.FINANCE:
    case ROLES.COMMUNITY_PRESIDENT:
      defineCommunityAbilities(builder, user)
      break
  }

  return new Ability(builder.rules) as BankAccountAbility
}

const defineVendorsAbilities = (
  builder: BankAccountAbilityBuilder,
  user: IUser
) => {
  builder.can(ACTIONS.VIEW, SUBJECTS.BANK_ACCOUNT_DETAILS)
  if (user.role === ROLES.VENDOR_CONTRACT_ADMIN) {
    builder.can(ACTIONS.EDIT, SUBJECTS.BANK_ACCOUNT_DETAILS)
    builder.can(ACTIONS.SEND, SUBJECTS.DEPOSIT, {
      ...isInStatuses([BANK_ACCOUNT_STATUS.CREATED]),
      ...isNotInReasons([
        BANK_ACCOUNT_DETAILS_STATUS_REASONS.ADMIN_ACTION,
        BANK_ACCOUNT_DETAILS_STATUS_REASONS.OTHER
      ])
    })
    builder.can(ACTIONS.VERIFY, SUBJECTS.DEPOSIT, {
      ...isInStatuses([BANK_ACCOUNT_STATUS.PENDING]),
      ...isNotInReasons([
        BANK_ACCOUNT_DETAILS_STATUS_REASONS.ADMIN_ACTION,
        BANK_ACCOUNT_DETAILS_STATUS_REASONS.OTHER
      ])
    })
    builder.can(ACTIONS.RESEND, SUBJECTS.DEPOSIT, {
      ...isInStatuses([BANK_ACCOUNT_STATUS.VERIFICATION_FAILED]),
      ...isInReasons([
        BANK_ACCOUNT_DETAILS_STATUS_REASONS.MICRO_DEPOSIT_EXPIRED,
        BANK_ACCOUNT_DETAILS_STATUS_REASONS.MICRO_DEPOSIT_RETURN,
        BANK_ACCOUNT_DETAILS_STATUS_REASONS.MICRO_DEPOSIT_ATTEMPTS_EXCEEDED
      ])
    })
  }
}

const defineCommunityAbilities = (
  builder: BankAccountAbilityBuilder,
  user: IUser
) => {
  builder.can(ACTIONS.VIEW, SUBJECTS.BANK_ACCOUNT_DETAILS)
  builder.can(ACTIONS.EDIT, SUBJECTS.BANK_ACCOUNT_DETAILS)
  builder.can(ACTIONS.SEND, SUBJECTS.DEPOSIT, {
    ...isInStatuses([BANK_ACCOUNT_STATUS.CREATED]),
    ...isNotInReasons([
      BANK_ACCOUNT_DETAILS_STATUS_REASONS.ADMIN_ACTION,
      BANK_ACCOUNT_DETAILS_STATUS_REASONS.OTHER
    ])
  })
  builder.can(ACTIONS.VERIFY, SUBJECTS.DEPOSIT, {
    ...isInStatuses([BANK_ACCOUNT_STATUS.PENDING]),
    ...isNotInReasons([
      BANK_ACCOUNT_DETAILS_STATUS_REASONS.ADMIN_ACTION,
      BANK_ACCOUNT_DETAILS_STATUS_REASONS.OTHER
    ])
  })
  builder.can(ACTIONS.RESEND, SUBJECTS.DEPOSIT, {
    ...isInStatuses([BANK_ACCOUNT_STATUS.VERIFICATION_FAILED]),
    ...isInReasons([
      BANK_ACCOUNT_DETAILS_STATUS_REASONS.MICRO_DEPOSIT_EXPIRED,
      BANK_ACCOUNT_DETAILS_STATUS_REASONS.MICRO_DEPOSIT_RETURN,
      BANK_ACCOUNT_DETAILS_STATUS_REASONS.MICRO_DEPOSIT_ATTEMPTS_EXCEEDED
    ])
  })
  if (
    user.role === ROLES.COMMUNITY_PRESIDENT ||
    user.role === ROLES.HOSPITAL_ADMIN
  ) {
    builder.can(ACTIONS.EDIT, SUBJECTS.ACCOUNT_PURPOSE, {
      ...isInStatuses([BANK_ACCOUNT_STATUS.VERIFIED])
    })
    builder.can(ACTIONS.SET_AS_DEFAULT, SUBJECTS.BANK_ACCOUNT_DETAILS, {
      ...isInStatuses([BANK_ACCOUNT_STATUS.VERIFIED]),
      ...isInPurposes([ACCOUNT_PURPOSE_OPTIONS.RECEIVABLE]),
      ...isNotDefault()
    })
  }
}
